import {useEffect, useState, Fragment } from "react";
import { styled, Theme, CSSObject, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Avatar, Menu, MenuItem, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
// Import missing icons
// import Flare from "@mui/icons-material/Flare";
// import NightsStay from "@mui/icons-material/NightsStay";
import PersonIcon from "@mui/icons-material/Person";
// import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
// import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { SupervisedUserCircle } from "@mui/icons-material";

// Import Redux related
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "./store";
import { clearUser } from "./slice/userSlice";

// Import other dependencies
import { routes } from "./routes";
import { post } from "./helpers/apisCalls";
import { LIGHT_MODE_THEME } from "./utils/constants";

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

// Styled components for the bottom section
const StyledListItemButton = styled(ListItemButton)<{ isEnabled: boolean }>(
  ({ theme, isEnabled }) => ({
    minHeight: 48,
    justifyContent: "initial",
    px: 2.5,
    opacity: isEnabled ? 1 : 0.5,
    cursor: isEnabled ? "pointer" : "not-allowed",
    "&:hover": {
      backgroundColor: isEnabled ? "rgba(255, 255, 255, 0.1)" : "transparent",
    },
  })
);

const StyledIconButton = styled(IconButton)<{ isSelected: boolean }>(
  ({ theme, isSelected }) => ({
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  })
);

const StyledIcon = styled("div")<{ component: React.ElementType; isSelected: boolean }>(
  ({ theme, isSelected }) => ({
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  })
);

export default function Sidebar() {
  const [open, setOpen] = useState(() => {
    return window.innerWidth >= 768; // false for mobile/tablet, true for desktop
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openEl = Boolean(anchorEl);
  
  const { user } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

   const [version, setVersion] = useState<string>('');
  
 useEffect(() => {
    // Dynamically import package.json
    import('../package.json').then(pkg => {
      setVersion(pkg.version);
    });
  }, []);

  if (!user) return <>loading...</>;

  const handleDrawerClose = () => {
    setOpen(!open);
  };

  // const handleClose = () => {
  //   setOpen(!open);
  // };

  // const toggleThemeMode = () => {
  //   // This function should dispatch an action to toggle theme mode
  //   // You'll need to implement this based on your theme management
  //   console.log("Toggle theme mode");
  // };

  const handleLogout = async () => {
    try {
      await post("/api/logout");
    } catch (error) {
      console.error("Logout error:", error);
    }
    navigate("/login");
    dispatch(clearUser());
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <ListItemText
            primary={"Hardware Controller"}
            sx={{ opacity: open ? 1 : 0 }}
          />
          <IconButton onClick={handleDrawerClose}>
            {open ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        
        {/* Main navigation routes */}
        <List>
          {routes.map((route, index) => (
            <Fragment key={route.path}>
              {route.showOnSidebar &&
                (route.access.includes(user?.role) ||
                  route.access.includes("*")) && (
                  <ListItem disablePadding sx={{ display: "block" }}>
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                      }}
                      onClick={() => {
                        navigate(route.path);
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        {route.icon && <route.icon />}
                      </ListItemIcon>
                      <ListItemText
                        primary={route.title}
                        sx={{ opacity: open ? 1 : 0 }}
                      />
                    </ListItemButton>
                  </ListItem>
                )}
            </Fragment>
          ))}
        </List>
        <Divider />

        {/* Bottom section with theme toggle, user info, and close button */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            p: 2,
          }}
        >
          <List sx={{ padding: 0 }}>
            {/* Theme toggle */}
            {/* <StyledListItemButton isEnabled={true} onClick={toggleThemeMode}>
              <ListItemIcon>
                <StyledIconButton size="small" isSelected={false}>
                  <StyledIcon 
                    component={theme.palette.mode === LIGHT_MODE_THEME ? Flare : NightsStay} 
                    isSelected={false} 
                  />
                </StyledIconButton>
              </ListItemIcon>
              {open && <ListItemText sx={{ color: '#ffffff' }} primary={'Theme Switch'} />}
            </StyledListItemButton> */}

            {/* User info */}
            <ListItemButton 
              sx={{
                minWidth: 100,
                minHeight: 48,
                justifyContent: open ? "initial" : "center",
                px: 2.5,
              }}
              onClick={(event: React.MouseEvent<HTMLDivElement>) => setAnchorEl(event.currentTarget)}
            >
              <ListItemIcon
                sx={{
                  minWidth: 100,
                  justifyContent: "center",
                  alignItems: 'center',
                  display: 'flex',
                }}
              >
                <SupervisedUserCircle />
                {open && <ListItemText sx={{ opacity: open ? 1 : 0, marginLeft: "8px" }} primary={'Preferences'} />}
              </ListItemIcon>
            </ListItemButton>
            <ListItemIcon>
              <StyledIconButton size="small" isSelected={false}>
                <StyledIcon component={PersonIcon} isSelected={false} />
              </StyledIconButton>
            </ListItemIcon>

            {/* Close/Open toggle */}
            {/* <StyledListItemButton isEnabled={true} onClick={() => handleClose()}>
              <ListItemIcon>
                <StyledIconButton size="small" isSelected={false}>
                  {open ? (
                    <StyledIcon component={ArrowBackIosRoundedIcon} isSelected={false} />
                  ) : (
                    <StyledIcon component={ArrowForwardIosRoundedIcon} isSelected={false} />
                  )}
                </StyledIconButton>
              </ListItemIcon>
              {open && <ListItemText sx={{ color: '#ffffff' }} primary={'Close'} />}
            </StyledListItemButton> */}
          </List>
        </Box>

        {/* User menu */}
        <Menu
          id="basic-menu"
          open={openEl}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem>
            <Avatar sx={{ width: 24, height: 24, mr: 1 }} /> {user?.username}
          </MenuItem>
          <MenuItem>Role: {user?.role}</MenuItem>
          <MenuItem onClick={handleLogout}>Log out</MenuItem>
          <MenuItem sx={{ textAlign: "center"}} disabled>
            <Typography align="center" variant="caption">
              Version: {version}
            </Typography>
          </MenuItem>
        </Menu>
      </Drawer>
    </Box>
  );
}